import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CalendarClock, CheckCircle2, CircleX, Clock, FileText,
  LayoutList, MessageSquare, Search,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { Booking, BookingStatus } from '../data/model'
import { nights, todayISO } from '../lib/dates'
import { Badge, Card, EmptyState, Input, cx } from '../ui/kit'
import BookingSheet, { STATUS_TONE } from './BookingSheet'

type Filter = 'upcoming' | 'all' | BookingStatus

/**
 * Filters are chips, not a dropdown: a dropdown hides both the options and the counts
 * behind a tap, and on a phone it opens a full-screen native picker for what is really
 * a one-tap decision.
 */
const FILTERS: { key: Filter; icon: typeof LayoutList }[] = [
  { key: 'upcoming', icon: CalendarClock },
  { key: 'all', icon: LayoutList },
  { key: 'enquiry', icon: MessageSquare },
  { key: 'quoted', icon: FileText },
  { key: 'option', icon: Clock },
  { key: 'confirmed', icon: CheckCircle2 },
  { key: 'cancelled', icon: CircleX },
]

export default function BookingsView() {
  const { t, formatMoney, formatDate, lang } = useI18n()
  const store = useStore()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('upcoming')
  const [active, setActive] = useState<Booking | null>(null)
  const de = lang === 'de'
  const today = todayISO()

  /**
   * `?buchung=<id>` öffnet die Buchung sofort zum Bearbeiten.
   *
   * Damit kommt man von einem Beleg mit einem Klick zurück in die Bearbeitung, statt
   * die Buchung im Kalender oder in der Liste wiederfinden zu müssen. Der Parameter
   * wird gleich wieder aus der Adresse genommen: sonst öffnet ein Zurück im Browser
   * dasselbe Blatt noch einmal, und ein Neuladen der Seite auch.
   */
  const [params, setParams] = useSearchParams()
  const wanted = params.get('buchung')
  useEffect(() => {
    if (!wanted) return
    const found = store.db.bookings.find((b) => b.id === wanted)
    if (found) {
      setActive(found)
      setFilter('all')
      setParams({}, { replace: true })
    }
  }, [wanted, store.db.bookings])

  const filterLabel = (f: Filter) =>
    f === 'upcoming' ? (de ? 'Aktuell' : 'Current')
    : f === 'all' ? (de ? 'Alle' : 'All')
    : t(`status.${f}` as never)

  const matchesFilter = (b: Booking, f: Filter) =>
    f === 'upcoming' ? b.departure >= today && b.status !== 'cancelled'
    : f === 'all' ? true
    : b.status === f

  const counts = useMemo(() => {
    const c = {} as Record<Filter, number>
    for (const { key } of FILTERS) {
      c[key] = store.db.bookings.filter((b) => matchesFilter(b, key)).length
    }
    return c
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.db.bookings, today])

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return store.db.bookings
      .filter((b) => matchesFilter(b, filter))
      .filter((b) => {
        if (!q) return true
        return [
          b.reference, store.guestName(b.guest_id),
          store.unitById(b.unit_id)?.name ?? '', b.internal_notes ?? '',
        ].join(' ').toLowerCase().includes(q)
      })
      .sort((a, b) => a.arrival.localeCompare(b.arrival))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, query, filter, today])

  const title = (b: Booking) =>
    b.kind === 'block' ? (b.internal_notes || t('status.block')) : store.guestName(b.guest_id)

  return (
    <div className="p-4 pt-safe space-y-4 max-w-6xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">{t('nav.bookings')}</h1>
        <div className="relative w-full max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input
            className="pl-9"
            placeholder={t('common.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Chips scroll sideways on a phone and wrap on a laptop — same control, and on
          touch a horizontal swipe is cheaper than opening a picker. */}
      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 scroll-x sm:overflow-visible">
        <div className="flex gap-2 sm:flex-wrap w-max sm:w-auto pb-1">
          {FILTERS.map(({ key, icon: Icon }) => {
            const on = filter === key
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                aria-pressed={on}
                className={cx(
                  'inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm',
                  'transition-colors whitespace-nowrap',
                  on
                    ? 'bg-accent text-accent-fg border-accent font-medium'
                    : 'bg-surface text-muted border-border hover:bg-surface-2',
                )}
              >
                <Icon size={16} />
                {filterLabel(key)}
                <span className={cx(
                  'tabular-nums text-xs rounded-full px-1.5 py-0.5',
                  on ? 'bg-black/15' : 'bg-surface-2',
                )}>
                  {counts[key]}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState title={t('common.noResults')} /></Card>
      ) : (
        <>
          {/* ---------------- phone: cards, thumb-sized targets ---------------- */}
          <div className="space-y-2 lg:hidden">
            {rows.map((b) => {
              const unit = store.unitById(b.unit_id)
              return (
                <button key={b.id} className="w-full text-left" onClick={() => setActive(b)}>
                  <Card className="p-3 flex items-center gap-3">
                    <span className="w-1.5 h-12 rounded-full shrink-0"
                      style={{ background: unit?.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{title(b)}</div>
                      <div className="text-xs text-muted truncate">
                        {formatDate(b.arrival)} – {formatDate(b.departure)}
                        {' · '}{nights(b.arrival, b.departure)} {t('common.nights')}
                      </div>
                      <div className="text-xs text-muted truncate">
                        {unit?.name} · {b.reference || '—'}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge tone={b.kind === 'block' ? 'neutral' : STATUS_TONE[b.status]}>
                        {b.kind === 'block' ? t('status.block') : t(`status.${b.status}` as never)}
                      </Badge>
                      <div className={cx('text-sm tabular-nums mt-1',
                        b.total_gross === 0 && 'text-muted')}>
                        {formatMoney(b.total_gross)}
                      </div>
                    </div>
                  </Card>
                </button>
              )
            })}
          </div>

          {/* ---------------- laptop: a real table ----------------
              A phone can only show three facts per row; a laptop can show eight, and
              scanning a season is what the desktop is actually for. */}
          <Card className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm min-w-[56rem]">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted border-b border-border">
                  <th className="text-left font-medium px-4 py-2.5">{t('booking.guest')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('booking.unit')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('booking.arrival')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('booking.departure')}</th>
                  <th className="text-right font-medium px-3 py-2.5">{t('common.nights')}</th>
                  <th className="text-right font-medium px-3 py-2.5">{t('booking.occupancy')}</th>
                  <th className="text-left font-medium px-3 py-2.5">{t('booking.channel')}</th>
                  <th className="text-left font-medium px-3 py-2.5">Status</th>
                  <th className="text-right font-medium px-4 py-2.5">{t('common.total')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const unit = store.unitById(b.unit_id)
                  return (
                    <tr
                      key={b.id}
                      onClick={() => setActive(b)}
                      className="border-b border-border last:border-0 cursor-pointer
                                 hover:bg-surface-2 transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium truncate max-w-[16rem]">{title(b)}</div>
                        <div className="text-xs text-muted">{b.reference || '—'}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: unit?.color }} />
                          {unit?.name}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">
                        {formatDate(b.arrival)}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">
                        {formatDate(b.departure)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {nights(b.arrival, b.departure)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {b.kind === 'block' ? '—' : b.adults + b.children}
                      </td>
                      <td className="px-3 py-2.5 text-muted whitespace-nowrap">
                        {t(`channel.${b.channel}` as never)}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge tone={b.kind === 'block' ? 'neutral' : STATUS_TONE[b.status]}>
                          {b.kind === 'block' ? t('status.block') : t(`status.${b.status}` as never)}
                        </Badge>
                      </td>
                      <td className={cx('px-4 py-2.5 text-right tabular-nums whitespace-nowrap',
                        b.total_gross === 0 && 'text-muted')}>
                        {formatMoney(b.total_gross)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <BookingSheet booking={active} open={!!active} onClose={() => setActive(null)} />
    </div>
  )
}
